import React, {
  forwardRef,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import axios from 'axios';
import MaterialTable from 'material-table';

import Button from '@material-ui/core/Button';
import AddBox from '@material-ui/icons/AddBox';
import ArrowUpward from '@material-ui/icons/ArrowUpward';
import Check from '@material-ui/icons/Check';
import ChevronLeft from '@material-ui/icons/ChevronLeft';
import ChevronRight from '@material-ui/icons/ChevronRight';
import Clear from '@material-ui/icons/Clear';
import DeleteOutline from '@material-ui/icons/DeleteOutline';
import Edit from '@material-ui/icons/Edit';
import FilterList from '@material-ui/icons/FilterList';
import FirstPage from '@material-ui/icons/FirstPage';
import InfoIcon from '@material-ui/icons/Info';
import LastPage from '@material-ui/icons/LastPage';
import Remove from '@material-ui/icons/Remove';
import SaveAlt from '@material-ui/icons/SaveAlt';
import Search from '@material-ui/icons/Search';
import ViewColumn from '@material-ui/icons/ViewColumn';
import {
  createMuiTheme,
  makeStyles,
  ThemeProvider,
} from '@material-ui/core/styles';

import { AuthorizeContext } from '../../RootContext';
import { api_url } from '../../authorize';

const tableIcons = {
  Add: forwardRef((props, ref) => <AddBox {...props} ref={ref} />),
  Check: forwardRef((props, ref) => <Check {...props} ref={ref} />),
  Clear: forwardRef((props, ref) => <Clear {...props} ref={ref} />),
  Delete: forwardRef((props, ref) => <DeleteOutline {...props} ref={ref} />),
  DetailPanel: forwardRef((props, ref) => <ChevronRight {...props} ref={ref} />),
  Edit: forwardRef((props, ref) => <Edit {...props} ref={ref} />),
  Export: forwardRef((props, ref) => <SaveAlt {...props} ref={ref} />),
  Filter: forwardRef((props, ref) => <FilterList {...props} ref={ref} />),
  FirstPage: forwardRef((props, ref) => <FirstPage {...props} ref={ref} />),
  LastPage: forwardRef((props, ref) => <LastPage {...props} ref={ref} />),
  NextPage: forwardRef((props, ref) => <ChevronRight {...props} ref={ref} />),
  PreviousPage: forwardRef((props, ref) => <ChevronLeft {...props} ref={ref} />),
  ResetSearch: forwardRef((props, ref) => <Clear {...props} ref={ref} />),
  Search: forwardRef((props, ref) => <Search {...props} ref={ref} />),
  Permissions: forwardRef((props, ref) => <InfoIcon {...props} ref={ref} />),
  SortArrow: forwardRef((props, ref) => <ArrowUpward {...props} ref={ref} />),
  ThirdStateCheck: forwardRef((props, ref) => <Remove {...props} ref={ref} />),
  ViewColumn: forwardRef((props, ref) => <ViewColumn {...props} ref={ref} />),
};

const theme = createMuiTheme({
  palette: {
    primary: {
      main: '#12899a',
      dark: '#0b6677',
      light: '#e4f4f6',
    },
  },
  typography: {
    fontFamily:
      '"Segoe UI Variable", "Segoe UI", Inter, -apple-system, BlinkMacSystemFont, Arial, sans-serif',
    h6: {
      fontSize: '1.25rem',
      fontWeight: 800,
    },
  },
  shape: {
    borderRadius: 10,
  },
});

const useStyles = makeStyles((muiTheme) => ({
  page: {
    minHeight: 'calc(100vh - 72px)',
    padding: '24px 26px 52px',
    boxSizing: 'border-box',
    color: '#1d394a',
    background:
      'radial-gradient(circle at 7% 0%, rgba(21, 157, 164, 0.15) 0, transparent 29%), radial-gradient(circle at 96% 4%, rgba(54, 107, 177, 0.12) 0, transparent 31%), linear-gradient(180deg, #f8fbfc 0%, #eaf3f6 100%)',

    [muiTheme.breakpoints.down('sm')]: {
      padding: '16px 11px 36px',
    },
  },

  shell: {
    width: '100%',
    maxWidth: 1480,
    margin: '0 auto',
  },

  hero: {
    position: 'relative',
    overflow: 'hidden',
    marginBottom: 18,
    padding: '25px 28px',
    borderRadius: 18,
    color: '#ffffff',
    background: 'linear-gradient(135deg, #16445f 0%, #0e7489 58%, #19988e 100%)',
    boxShadow: '0 18px 42px rgba(20, 70, 93, 0.18)',

    '&::after': {
      content: '""',
      position: 'absolute',
      width: 260,
      height: 260,
      right: -92,
      top: -135,
      border: '34px solid rgba(255, 255, 255, 0.07)',
      borderRadius: '50%',
      pointerEvents: 'none',
    },
  },

  heroEyebrow: {
    position: 'relative',
    zIndex: 1,
    margin: 0,
    color: 'rgba(255, 255, 255, 0.72)',
    fontSize: 12,
    fontWeight: 900,
    letterSpacing: '0.14em',
    textTransform: 'uppercase',
  },

  heroTitle: {
    position: 'relative',
    zIndex: 1,
    margin: '6px 0 0',
    fontSize: 32,
    lineHeight: 1.15,
    fontWeight: 850,
    letterSpacing: '-0.035em',
  },

  heroSubtitle: {
    position: 'relative',
    zIndex: 1,
    maxWidth: 760,
    margin: '8px 0 0',
    color: 'rgba(255, 255, 255, 0.84)',
    fontSize: 15,
    lineHeight: 1.5,
  },

  uploadCard: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 18,
    marginBottom: 18,
    padding: '18px 20px',
    border: '1px solid #d5e5ea',
    borderRadius: 16,
    background: '#ffffff',
    boxShadow: '0 13px 32px rgba(24, 72, 94, 0.08)',

    [muiTheme.breakpoints.down('sm')]: {
      alignItems: 'stretch',
      flexDirection: 'column',
    },
  },

  uploadCopy: {
    minWidth: 0,
  },

  uploadTitle: {
    margin: 0,
    color: '#173d50',
    fontSize: 18,
    fontWeight: 800,
  },

  uploadText: {
    margin: '4px 0 0',
    color: '#667f8d',
    fontSize: 14,
    lineHeight: 1.45,
  },

  uploadActions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 12,
    minWidth: 0,

    [muiTheme.breakpoints.down('sm')]: {
      justifyContent: 'stretch',
      flexWrap: 'wrap',

      '& > *': {
        flex: '1 1 180px',
      },
    },
  },

  filePicker: {
    minWidth: 175,
    minHeight: 52,
    padding: '0 24px !important',
    border: '2px solid #9ecbd5 !important',
    borderRadius: '11px !important',
    color: '#075f73 !important',
    background: '#eaf7f9 !important',
    boxShadow: '0 8px 18px rgba(18, 111, 132, 0.10) !important',
    fontSize: '16px !important',
    fontWeight: '850 !important',
    letterSpacing: '0.01em',
    textTransform: 'none !important',

    '&:hover': {
      borderColor: '#67adba !important',
      background: '#dff2f5 !important',
      boxShadow: '0 10px 22px rgba(18, 111, 132, 0.15) !important',
    },
  },

  hiddenInput: {
    display: 'none',
  },

  fileName: {
    display: 'flex',
    alignItems: 'center',
    minWidth: 220,
    minHeight: 52,
    maxWidth: 420,
    overflow: 'hidden',
    padding: '0 16px',
    border: '1px solid #cfdee4',
    borderRadius: 11,
    color: '#2f5061',
    background: '#f8fbfc',
    fontSize: 15,
    fontWeight: 700,
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',

    [muiTheme.breakpoints.down('sm')]: {
      flex: '1 1 220px',
    },
  },

  uploadButton: {
    minWidth: 135,
    minHeight: 52,
    padding: '0 26px !important',
    borderRadius: '11px !important',
    boxShadow: '0 10px 22px rgba(18, 137, 154, 0.24) !important',
    fontSize: '16px !important',
    fontWeight: '850 !important',
    letterSpacing: '0.01em',
    textTransform: 'none !important',

    '&:hover': {
      boxShadow: '0 12px 26px rgba(18, 137, 154, 0.30) !important',
    },

    '&.Mui-disabled': {
      color: '#8b999f !important',
      background: '#dfe5e8 !important',
      boxShadow: 'none !important',
    },
  },

  tableCard: {
    overflow: 'hidden',
    border: '1px solid #d5e5ea',
    borderTop: '4px solid #168fa0',
    borderRadius: 17,
    background: '#ffffff',
    boxShadow: '0 16px 38px rgba(24, 72, 94, 0.10)',

    '& .MuiPaper-root': {
      width: '100% !important',
      maxWidth: '100% !important',
      margin: '0 !important',
      borderRadius: '0 !important',
      boxShadow: 'none !important',
      background: '#ffffff !important',
    },

    '& .MuiToolbar-root': {
      minHeight: '72px !important',
      padding: '0 22px !important',
      borderBottom: '1px solid #d5e7ec',
      background: 'linear-gradient(90deg, #edf8fa 0%, #f1f6fb 100%)',
    },

    '& .MuiToolbar-root h6': {
      color: '#0f5264',
      fontSize: '21px !important',
      fontWeight: '850 !important',
      letterSpacing: '-0.02em',
    },

    '& .MuiToolbar-root .MuiInputBase-root': {
      minHeight: 42,
      padding: '0 11px',
      border: '1px solid #bdd8df',
      borderRadius: 9,
      color: '#294958',
      background: '#ffffff',
      boxShadow: '0 3px 10px rgba(24, 92, 111, 0.06)',
      fontSize: 14,
    },

    '& .MuiInput-underline:before, & .MuiInput-underline:after': {
      display: 'none',
    },

    '& .MuiTableContainer-root': {
      width: '100% !important',
      overflowX: 'hidden !important',
    },

    '& .MuiTable-root': {
      width: '100% !important',
      tableLayout: 'fixed !important',
    },

    '& .MuiTableCell-root': {
      minWidth: '0 !important',
      paddingLeft: '20px !important',
      paddingRight: '20px !important',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },

    '& .MuiTableCell-head': {
      paddingTop: '16px !important',
      paddingBottom: '16px !important',
      borderBottom: '1px solid #bddbe2 !important',
      color: '#123f52 !important',
      background: 'linear-gradient(180deg, #dff1f5 0%, #eaf4f8 100%) !important',
      fontSize: '16px !important',
      fontWeight: '850 !important',
      lineHeight: '1.25 !important',
      whiteSpace: 'nowrap !important',
    },

    '& .MuiTableCell-body': {
      paddingTop: '17px !important',
      paddingBottom: '17px !important',
      borderBottom: '1px solid #e5eef1 !important',
      color: '#29414f',
      fontSize: '15px !important',
      fontWeight: '550 !important',
      lineHeight: '1.45 !important',
      whiteSpace: 'nowrap !important',
    },

    '& .MuiTableRow-root:nth-of-type(even) .MuiTableCell-body': {
      background: '#f8fbfc',
    },

    '& .MuiTableRow-root:hover .MuiTableCell-body': {
      background: '#eaf6f8 !important',
    },

    '& .MuiTableCell-head:last-child, & .MuiTableCell-body:last-child': {
      width: '130px !important',
      textAlign: 'center !important',
    },

    '& .MuiTableCell-body:last-child > div': {
      display: 'flex !important',
      alignItems: 'center !important',
      justifyContent: 'center !important',
    },

    '& .MuiIconButton-root': {
      width: 42,
      height: 42,
      padding: '9px !important',
      color: '#34788a',
      transition: 'background-color 130ms ease, color 130ms ease, transform 130ms ease',
    },

    '& .MuiIconButton-root .MuiSvgIcon-root': {
      fontSize: '23px !important',
    },

    '& .MuiIconButton-root:hover': {
      color: '#075f75',
      background: '#dff1f5',
      transform: 'translateY(-1px)',
    },

    '& .MuiTablePagination-root': {
      borderTop: '1px solid #d9e8ec',
      color: '#476979',
      background: '#f8fbfc',
    },

    '& .MuiTablePagination-toolbar': {
      minHeight: '62px !important',
      padding: '0 16px !important',
    },

    '& .MuiTablePagination-caption, & .MuiTablePagination-select': {
      fontSize: '13px !important',
      fontWeight: '700 !important',
    },
  },
}));

const ImplantList = () => {
  const classes = useStyles();
  const {
    token,
    authBody,
    setAuthBody,
    setAuthenticated,
  } = useContext(AuthorizeContext);

  const [profile] = useState(authBody || { role: 'visitor' });
  const [file, setFile] = useState({ selectedFile: null });
  const [loading, setLoading] = useState(true);
  const [state, setState] = useState({
    columns: [
      {
        title: 'Company Name',
        field: 'company_name',
        filtering: false,
        cellStyle: { fontSize: 15 },
      },
      {
        title: 'Product',
        field: 'product_name',
        filtering: false,
        cellStyle: { fontSize: 15 },
      },
      {
        title: 'Body Part',
        field: 'body_key',
        filtering: false,
        cellStyle: { fontSize: 15 },
      },
    ],
    data: [],
  });

  const canManageImplants = ['super_admin', 'write_admin', 'read_admin'].includes(
    profile.role
  );

  const fileName = file.selectedFile ? file.selectedFile.name : '';

  const expireSession = () => {
    setAuthenticated(false);
    setAuthBody({ role: 'visitor' });
    alert('You have been logged out due to inactivity. Please log in again.');
  };

  useEffect(() => {
    let mounted = true;

    axios({
      url: `https://${api_url}/api/implant_info`,
      method: 'get',
      headers: {
        'x-access-token': token,
      },
    })
      .then((response) => {
        if (!mounted) return;

        if (response.data.message) {
          alert(response.data.message);
        } else if (response.data.notLoggedIn) {
          expireSession();
        } else {
          setState((previous) => ({
            ...previous,
            data: Array.isArray(response.data) ? response.data : [],
          }));
        }
      })
      .catch((error) => {
        if (mounted) {
          alert(error.message || error);
        }
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, []);

  const onFileChange = (event) => {
    const selectedFile = event.target.files && event.target.files[0]
      ? event.target.files[0]
      : null;

    setFile({ selectedFile });
  };

  const onFileUpload = () => {
    if (!file.selectedFile) return;

    const txtFile = new FormData();
    txtFile.append('Implant', file.selectedFile, file.selectedFile.name);

    axios({
      url: `https://${api_url}/api/implant_info`,
      method: 'post',
      data: txtFile,
      headers: {
        'x-access-token': token,
      },
    })
      .then((response) => {
        if (response.data.message) {
          alert(response.data.message);
        } else if (response.data.notLoggedIn) {
          expireSession();
        } else {
          setFile({ selectedFile: null });
          window.location.reload();
        }
      })
      .catch((error) => alert(error.message || error));
  };

  const deleteImplant = (oldData) =>
    new Promise((resolve, reject) => {
      const dataDelete = [...state.data];
      const index = oldData.tableData.id;

      axios({
        url: `https://${api_url}/api/implant_info?id=${oldData.id}`,
        method: 'delete',
        headers: {
          'x-access-token': token,
        },
      })
        .then((response) => {
          if (response.data.message) {
            alert(response.data.message);
            window.location.reload();
            resolve();
            return;
          }

          if (response.data.notLoggedIn) {
            expireSession();
            resolve();
            return;
          }

          dataDelete.splice(index, 1);
          setState((previous) => ({
            ...previous,
            data: dataDelete,
          }));
          resolve();
        })
        .catch((error) => {
          alert(error.message || error);
          reject(error);
        });
    });

  const localization = useMemo(
    () => ({
      pagination: {
        labelDisplayedRows: '{from}-{to} of {count}',
      },
      toolbar: {
        nRowsSelected: '{0} row(s) selected',
      },
      header: {
        actions: 'Actions',
      },
      body: {
        emptyDataSourceMessage: 'No implant records to display',
        filterRow: {
          filterTooltip: 'Filter',
        },
      },
    }),
    []
  );

  const tableOptions = useMemo(
    () => ({
      actionsColumnIndex: -1,
      sorting: true,
      search: true,
      paging: true,
      pageSize: 10,
      pageSizeOptions: [5, 10, 20, 50],
      showFirstLastPageButtons: true,
      headerStyle: {
        fontSize: 16,
      },
      rowStyle: {
        fontSize: 15,
      },
    }),
    []
  );

  return (
    <ThemeProvider theme={theme}>
      <main className={classes.page}>
        <div className={classes.shell}>
          <section className={classes.hero}>
            <p className={classes.heroEyebrow}></p>
            <h1 className={classes.heroTitle}>Implant </h1>
          </section>

          <section className={classes.uploadCard}>
            <div className={classes.uploadCopy}>
              <h2 className={classes.uploadTitle}>Upload implant data</h2>

            </div>

            <div className={classes.uploadActions}>
              <Button
                className={classes.filePicker}
                component="label"
                variant="outlined"
              >
                Choose file
                <input
                  className={classes.hiddenInput}
                  type="file"
                  name="file"
                  onChange={onFileChange}
                />
              </Button>

              <div className={classes.fileName} title={fileName || 'No file selected'}>
                {fileName || 'No file selected'}
              </div>

              <Button
                className={classes.uploadButton}
                disabled={!fileName}
                color="primary"
                variant="contained"
                onClick={onFileUpload}
              >
                Upload
              </Button>
            </div>
          </section>

          <section className={classes.tableCard}>
            <MaterialTable
              title={canManageImplants ? 'Implant' : 'Implants'}
              columns={state.columns}
              data={state.data}
              icons={tableIcons}
              isLoading={loading}
              localization={localization}
              options={tableOptions}
              editable={
                canManageImplants
                  ? {
                      onRowDelete: deleteImplant,
                    }
                  : undefined
              }
            />
          </section>
        </div>
      </main>
    </ThemeProvider>
  );
};

export default ImplantList;